import { all, call, delay, put, select } from 'redux-saga/effects';
import { ApiError } from 'store/api/types';
import { FetchViesInfoRequestAction } from '../types';
import { checkResponse } from 'store/utils';
import moment from 'moment';
import { GetViesInfoResponse, getViesInfo } from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { fetchViesInfoFailure, fetchViesInfoSuccess } from '../actions';
import {
  getOrgLoading,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { Organisation } from 'store/organisation/models';
import { fetchOrganisationsRequest } from 'store/organisation/actions';
import { setGettingTaxes } from 'store/tax-rates/actions';
import { ViesInfo } from '../models';
import { getAuthUser } from 'store/auth/selectors';
import { User } from 'store/auth/models';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { Provider } from 'store/api/enums';
import i18n from 'i18next';
interface SavedInfos {
  timestamp: string;
  viesInfos: ViesInfo[];
}

const EXCLUDED_VIES_PROVIDERS = [Provider.GROUP, Provider.HMRC];

const isProviderExcludedFromVies = (providerName?: string): boolean =>
  EXCLUDED_VIES_PROVIDERS.includes(providerName?.toLowerCase() as Provider);

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchViesInfoSaga(action: FetchViesInfoRequestAction) {
  const { connectionId, immediateRefresh } = action.payload;
  const REFRESH_TIMEOUT = immediateRefresh ? 0 : 15;
  let currentUser: User = yield select(getAuthUser);
  do {
    yield delay(100);
    currentUser = yield select(getAuthUser);
  } while (!currentUser);

  const fileName = `vies-infos-${currentUser.userUuid}`;
  try {
    let err: ApiError | null;

    let orgs: Organisation[] = yield select(getOrgOrganisations);

    if (!orgs) {
      yield put(fetchOrganisationsRequest());
      let orgsLoading: boolean = yield select(getOrgLoading);
      do {
        yield delay(100);
        orgs = yield select(getOrgOrganisations);
        orgsLoading = yield select(getOrgLoading);
      } while (!orgs && orgsLoading);

      //orgs = yield select(getOrgOrganisations);
    }

    if (orgs) {
      if (connectionId) {
        const selOrg = orgs.find((o) => o.connectionId === connectionId);
        if (!selOrg) {
          const apiError = {
            message: i18n.t('common.problem-loading-the-entity'),
          };
          throw apiError;
        }

        if (isProviderExcludedFromVies(selOrg.providerName)) {
          return;
        }

        const response: GetViesInfoResponse = yield call(
          getViesInfo,
          connectionId
        );

        err = checkResponse(response?.responseMessage);
        if (err) throw err;
        const organisationName = selOrg.friendlyName;
        const vatCheckerActive = selOrg.vatCheckerActive;
        const providerName = selOrg.providerName;
        const shortCode = selOrg.shortCode;
        yield put(
          fetchViesInfoSuccess([
            {
              ...response.data,
              organisationName,
              vatCheckerActive,
              providerName,
              shortCode,
            },
          ])
        );
      } else {
        const viesEligibleOrgs = orgs.filter(
          (org) => !isProviderExcludedFromVies(org.providerName)
        );

        const savedViesInfos = localStorage.getItem(fileName);
        const savedInfos: SavedInfos = savedViesInfos
          ? JSON.parse(savedViesInfos)
          : null;

        const infos = savedInfos?.viesInfos?.filter(
          (info) => !isProviderExcludedFromVies(info.providerName)
        );
        const timestamp = savedInfos?.timestamp
          ? moment(savedInfos?.timestamp)
          : null;

        if (!timestamp) {
          localStorage.removeItem(fileName);
        }

        const timeToRefresh = timestamp
          ? moment().isAfter(timestamp.add(REFRESH_TIMEOUT, 'minutes'))
          : true;
        if (!timeToRefresh) {
          // If cached data is still fresh, hydrate state once and avoid API calls.
          if (infos && infos.length) {
            yield put(fetchViesInfoSuccess(infos));
          }
        } else {
          // Fetch all entities in parallel so load time ≈ slowest request
          // instead of sum of all requests (sequential).
          yield put(setGettingTaxes(true));

          const responses: (GetViesInfoResponse | null)[] = yield all(
            viesEligibleOrgs.map((org) =>
              call(function* safeGetViesInfo() {
                try {
                  return (yield call(
                    getViesInfo,
                    org.connectionId
                  )) as GetViesInfoResponse;
                } catch {
                  return null;
                }
              })
            )
          );

          const newViesInfos: ViesInfo[] = viesEligibleOrgs.reduce(
            (acc: ViesInfo[], org, i) => {
              const response = responses[i];
              if (!response) return acc;

              err = checkResponse(response.responseMessage);
              if (err) return acc;

              acc.push({
                ...response.data,
                organisationName: org.friendlyName,
                vatCheckerActive: org.vatCheckerActive,
                providerName: org.providerName,
                shortCode: org.shortCode,
              });

              return acc;
            },
            []
          );

          yield put(fetchViesInfoSuccess(newViesInfos));

          newViesInfos.length > 0
            ? localStorage.setItem(
                fileName,
                JSON.stringify({
                  timestamp: moment(),
                  viesInfos: newViesInfos,
                })
              )
            : localStorage.removeItem(fileName);
          yield put(setGettingTaxes(false));
        }
      }
    }
  } catch (error: any) {
    yield put(setGettingTaxes(false));
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchViesInfoFailure());
  }
}
